import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageDropdown from '../components/LanguageDropdown';
import AuthModal from '../components/AuthModal';

const Container = styled.div`
  min-height: 100vh;
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  position: relative;
  width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }
`;

const Title = styled.h1`
  font-size: 3rem;
  color: #ffffff;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.3s ease;
  text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.7);
  
  &:hover {
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const UserSection = styled.div`
  position: fixed;
  top: 20px;
  left: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  z-index: 1000;
  
  @media (max-width: 768px) {
    top: 15px;
    left: 15px;
    gap: 10px;
  }
  
  @media (max-width: 480px) {
    top: 10px;
    left: 10px;
    flex-direction: column;
    gap: 8px;
  }
`;

const LanguageSwitcher = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 999;
  
  @media (max-width: 768px) {
    top: 15px;
    right: 15px;
  }
  
  @media (max-width: 480px) {
    top: 10px;
    right: 10px;
  }
`;

const AuthButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: var(--color-dark);
  padding: 10px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  cursor: pointer;
  font-weight: 600;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    padding: 8px 12px;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--color-dark);
  font-weight: 600;
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--color-primary);
  color: var(--color-light-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.9rem;
  
  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
    font-size: 0.8rem;
  }
`;

const UserButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: var(--color-dark);
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 0.75rem;
  cursor: pointer;
  font-weight: 600;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
  }
  
  @media (max-width: 480px) {
    font-size: 0.7rem;
    padding: 5px 10px;
  }
`;

const CartButton = styled(motion.button)`
  position: relative;
  padding: 12px 24px;
  background: linear-gradient(45deg, #4ecdc4, #45b7d1);
  color: white;
  border: none;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(78, 205, 196, 0.4);
  }
`;

const CartBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background: #e74c3c;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: bold;
`;

const PackagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-bottom: 40px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 25px;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const PackageCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  padding: 30px;
  position: relative;
  border: 2px solid ${props => props.popular ? '#4ecdc4' : 'rgba(255, 255, 255, 0.2)'};
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
  color: #2c3e50;
  
  ${props => props.popular && `
    transform: scale(1.05);
    box-shadow: 0 20px 50px rgba(78, 205, 196, 0.3);
  `}
`;

const Badge = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(45deg, #ff6b6b, #4ecdc4);
  color: white;
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: bold;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
`;

const PackageName = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 10px;
  color: #2c3e50;
  font-weight: 700;
`;

const PackageDescription = styled.p`
  color: #7f8c8d;
  margin-bottom: 20px;
  line-height: 1.5;
`;

const PriceContainer = styled.div`
  margin-bottom: 25px;
`;

const Price = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  color: #2c3e50;
  display: flex;
  align-items: baseline;
  gap: 5px;
  
  span {
    font-size: 1.2rem;
    color: #7f8c8d;
  }
`;

const OriginalPrice = styled.div`
  font-size: 1.2rem;
  color: #e74c3c;
  text-decoration: line-through;
  margin-top: 5px;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 25px 0;
`;

const Feature = styled.li`
  padding: 8px 0;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &:before {
    content: "✓";
    color: #4ecdc4;
    font-weight: bold;
    font-size: 1.2rem;
  }
`;

const AddToCartButton = styled(motion.button)`
  width: 100%;
  padding: 15px;
  background: ${props => props.inCart ? '#95a5a6' : 'linear-gradient(45deg, #4ecdc4, #45b7d1)'};
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const PaymentPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const { 
    packages, 
    cartItems, 
    cartItemCount, 
    addToCart, 
    setIsCartOpen,
    isCartOpen 
  } = useCart();

  const [loadingPackageId, setLoadingPackageId] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const handleAddToCart = async (packageItem) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    setLoadingPackageId(packageItem.id);
    
    try {
      addToCart(packageItem);
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Failed to add item to cart. Please try again.');
    } finally {
      setLoadingPackageId(null);
    }
  };

  const isInCart = (packageId) => {
    return cartItems.some(item => item.id === packageId);
  };

  return (
    <Container>
      {/* User Authentication Section */}
      <UserSection>
        {user ? (
          <>
            <UserInfo>
              <UserAvatar>
                {user.full_name ? user.full_name.charAt(0).toUpperCase() : 'U'}
              </UserAvatar>
              <span>Hi, {user.full_name || 'User'}!</span>
            </UserInfo>
            <UserButton onClick={logout}>Logout</UserButton>
          </>
        ) : (
          <AuthButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowAuthModal(true)}
          >
            Sign In / Sign Up
          </AuthButton>
        )}
      </UserSection>

      {/* Language Switcher */}
      <LanguageSwitcher>
        <LanguageDropdown />
      </LanguageSwitcher>

      <Header>
        <Title onClick={() => navigate('/')}>
          {t('paymentTitle')}
        </Title>
        
        <CartButton
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsCartOpen(true)}
        >
          🛒 {t('cart')}
          {cartItemCount > 0 && (
            <CartBadge>{cartItemCount}</CartBadge>
          )}
        </CartButton>
      </Header>

      <PackagesGrid>
        {packages.map((pkg) => (
          <PackageCard
            key={pkg.id}
            popular={pkg.badge === 'Most Popular'}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            whileHover={{ y: -5 }}
          >
            {pkg.badge && <Badge>{pkg.badge}</Badge>}
            
            <PackageName>{pkg.name}</PackageName>
            <PackageDescription>{pkg.description}</PackageDescription>
            
            <PriceContainer>
              <Price>
                ${pkg.price}
                {pkg.interval && <span>/{pkg.interval}</span>}
              </Price>
              {pkg.originalPrice && (
                <OriginalPrice>
                  Was ${pkg.originalPrice}
                </OriginalPrice>
              )}
            </PriceContainer>
            
            <FeaturesList>
              {pkg.features.map((feature, index) => (
                <Feature key={index}>{feature}</Feature>
              ))}
            </FeaturesList>
            
            <AddToCartButton
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleAddToCart(pkg)}
              disabled={loadingPackageId === pkg.id}
              inCart={isInCart(pkg.id)}
            >
              {loadingPackageId === pkg.id ? (
                t('adding')
              ) : isInCart(pkg.id) ? (
                t('addedToCart')
              ) : (
                t('addToCart')
              )}
            </AddToCartButton>
          </PackageCard>
        ))}
      </PackagesGrid>

      <div style={{
        textAlign: 'center',
        padding: '40px 20px',
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '20px',
        backdropFilter: 'blur(10px)'
      }}>
        <h3 style={{ color: 'white', marginBottom: '15px' }}>
          {t('securePayment')}
        </h3>
        <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem' }}>
          {t('securePaymentDesc')}
        </p>
      </div>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultTab="signup"
      />
    </Container>
  );
};

export default PaymentPage; 